<template>
    <Card :pageTitle="`Transfer Cash from ${safe.name}`">
        <div v-if="loading">Loading...</div>
        <div v-else>
            <div class="mb-3">
                <Button noMargin @click="$router.push(`/safe-drawers/${safeId}`)">
                    <i class="fas fa-chevron-left"></i> Back To Safe
                </Button>
            </div>

            <!-- Transfer Info Card -->
            <div class="transfer-info-card">
                <div class="endpoint-box from">
                    <div class="label">FROM</div>
                    <div class="name">{{ safe.name }}</div>
                    <div class="balance">Available: ${{ round2(safeOnHandCash) }}</div>
                </div>
                <div class="arrow">→</div>
                <div class="endpoint-box to">
                    <div class="label">TO</div>
                    <select v-model="selectedDrawerId" class="drawer-select">
                        <option value="">Select a drawer...</option>
                        <option v-for="drawer in availableDrawers" :key="drawer.id" :value="drawer.id">
                            {{ drawer.name }} ({{ drawer.isOpen ? 'Open' : 'Closed' }})
                        </option>
                    </select>
                </div>
            </div>

            <div v-if="selectedDrawerId" class="transfer-form">
                <!-- Cash Denomination Form -->
                <div class="row">
                    <div class="col-md-6">
                        <h5>Enter Cash Amount</h5>
                        <CashDenominationForm
                            :showExpectedTotal="false"
                            @change="handleCashChange"
                        />
                    </div>
                    <div class="col-md-6">
                        <div class="transfer-summary-box">
                            <h5>Transfer Summary</h5>
                            <div class="summary-row">
                                <span>Transfer Amount:</span>
                                <strong class="amount">${{ round2(transferAmount) }}</strong>
                            </div>
                            <div class="summary-row">
                                <span>Safe After Transfer:</span>
                                <span>${{ round2(safeOnHandCash - transferAmount) }}</span>
                            </div>
                            <div v-if="transferAmount > safeOnHandCash" class="alert-danger">
                                ⚠️ Insufficient funds in safe!
                            </div>
                        </div>

                        <CustomInput 
                            label="Notes (optional)" 
                            type="textarea" 
                            v-model="notes" 
                            placeholder="Add any notes about this transfer..."
                        />
                    </div>
                </div>

                <!-- Action Buttons -->
                <div class="action-buttons">
                    <Button @click="$router.push(`/safe-drawers/${safeId}`)" borderOnly class="me-2">Cancel</Button>
                    <Button 
                        @click="handleTransfer" 
                        :disabled="!canTransfer || processing"
                    >
                        {{ processing ? "Transferring..." : "Transfer Cash" }}
                    </Button>
                </div>
            </div>
        </div>
    </Card>
</template>

<script>
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CashDenominationForm from "../../components/cash-management/CashDenominationForm.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import CashManagementFetchApi from "../../utils/fetch/cash-management-fetch-api"
import { http } from "@/utils/http"
import { round2, errMsg } from "../../utils/common"

export default {
    name: "SafeDrawerTransfer",
    components: {
        Card,
        Button,
        CashDenominationForm,
        CustomInput,
    },
    data() {
        return {
            loading: false,
            processing: false,
            safeId: this.$route.params.id,
            safe: null,
            drawers: [],
            selectedDrawerId: "",
            transferAmount: 0,
            denominations: {},
            notes: "",
            safeOnHandCash: 0,
        }
    },
    computed: {
        availableDrawers() {
            // Show all drawers (both open and closed) for flexibility
            return this.drawers
        },
        canTransfer() {
            return (
                this.selectedDrawerId &&
                this.transferAmount > 0 &&
                this.transferAmount <= this.safeOnHandCash &&
                !this.processing
            )
        },
    },
    async mounted() {
        await this.fetchData()
    },
    methods: {
        round2,

        /**
         * Fetch safe details and available drawers
         */
        async fetchData() {
            this.loading = true
            try {
                // Fetch safe details
                this.safe = await CashManagementFetchApi.fetchSafeOrDrawerDetail(this.safeId)
                
                if (this.safe.parent) {
                    this.$toast.error("Can only transfer from a safe, not a drawer")
                    this.$router.push("/cash-management")
                    return
                }

                // Fetch safe summary to get safeOnHandCash
                const { result } = await CashManagementFetchApi.fetchSafeOrDrawerSummary(this.safeId)
                this.safeOnHandCash = result.safeOnHandCash || 0

                // Fetch child drawers
                this.drawers = await CashManagementFetchApi.fetchSafeOrDrawers(this.safeId)

            } catch (err) {
                console.error("Error fetching data:", err)
                this.$toast.error("Failed to load safe details")
            }
            this.loading = false
        },

        /**
         * Handle cash denomination changes from the form
         */
        handleCashChange(data) {
            this.denominations = data.denominations
            this.transferAmount = data.totalAmount
        },

        /**
         * Execute the cash transfer
         */
        async handleTransfer() {
            if (!this.canTransfer) return

            this.processing = true
            try {
                const transferData = {
                    source_id: this.safeId,
                    destination_id: this.selectedDrawerId,
                    amount: this.transferAmount,
                    denominations: {
                        cash_100_bill_count: Number(this.denominations.cash_100_bill_count?.amount || 0),
                        cash_50_bill_count: Number(this.denominations.cash_50_bill_count?.amount || 0),
                        cash_20_bill_count: Number(this.denominations.cash_20_bill_count?.amount || 0),
                        cash_10_bill_count: Number(this.denominations.cash_10_bill_count?.amount || 0),
                        cash_5_bill_count: Number(this.denominations.cash_5_bill_count?.amount || 0),
                        cash_2_bill_count: Number(this.denominations.cash_2_bill_count?.amount || 0),
                        cash_1_bill_count: Number(this.denominations.cash_1_bill_count?.amount || 0),
                        cash_25_cent_count: Number(this.denominations.cash_25_cent_count?.amount || 0),
                        cash_10_cent_count: Number(this.denominations.cash_10_cent_count?.amount || 0),
                        cash_5_cent_count: Number(this.denominations.cash_5_cent_count?.amount || 0),
                        cash_1_cent_count: Number(this.denominations.cash_1_cent_count?.amount || 0),
                    },
                    notes: this.notes,
                }

                const response = await http.post("/safe-drawers/cash-transfer", transferData)

                this.$toast.success(
                    response.data.message || 
                    `Successfully transferred $${round2(this.transferAmount)}`
                )

                // Redirect back to safe detail
                this.$router.push(`/safe-drawers/${this.safeId}`)

            } catch (error) {
                console.error("Transfer error:", error)
                this.$toast.error(errMsg(error, "Failed to transfer cash"))
            } finally {
                this.processing = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.transfer-info-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    padding: 2rem;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border-radius: 12px;
    margin-bottom: 2rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.endpoint-box {
    flex: 1;
    text-align: center;
    padding: 1.5rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &.from {
        border-left: 4px solid #2196f3;
    }

    &.to {
        border-left: 4px solid #4caf50;
    }

    .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6c757d;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 0.5rem;
    }

    .name {
        font-size: 1.25rem;
        font-weight: 600;
        color: #212529;
        margin-bottom: 0.25rem;
    }

    .balance {
        font-size: 0.9rem;
        color: #28a745;
        font-weight: 500;
    }

    .drawer-select {
        width: 100%;
        padding: 0.75rem;
        font-size: 1rem;
        border: 2px solid #dee2e6;
        border-radius: 6px;
        background: white;
        cursor: pointer;
        transition: border-color 0.15s ease-in-out;

        &:focus {
            border-color: #4caf50;
            outline: none;
        }
    }
}

.arrow {
    font-size: 2.5rem;
    color: #28a745;
    font-weight: bold;
}

.transfer-form {
    margin-top: 2rem;

    h5 {
        margin-bottom: 1rem;
        color: #495057;
        font-weight: 600;
    }
}

.transfer-summary-box {
    padding: 1.5rem;
    background-color: #e7f3ff;
    border-radius: 8px;
    border-left: 4px solid #007bff;
    margin-bottom: 1.5rem;

    h5 {
        margin-bottom: 1rem;
        color: #007bff;
    }

    .summary-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;

        &:last-child {
            margin-bottom: 0;
        }

        .amount {
            font-size: 1.5rem;
            color: #007bff;
        }
    }

    .alert-danger {
        margin-top: 1rem;
        padding: 0.75rem;
        background-color: #f8d7da;
        color: #721c24;
        border-radius: 6px;
        font-weight: 600;
    }
}

.action-buttons {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
}
</style>
